import { useEffect, useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { useHomeStore } from "../../store/homeStore";
import { useAuthStore } from "../../store/authStore";
// 아이콘 import
import {
	FaTicketAlt,
	FaChair,
	FaClock,
	FaRegListAlt,
	FaRegCalendarCheck,
	FaHistory,
	FaChartBar,
	FaBus,
} from "react-icons/fa";
import { LuTrainFront } from "react-icons/lu";
import { GiBookshelf } from "react-icons/gi";
import { IoCalendarNumberOutline, IoTicketOutline } from "react-icons/io5";
import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	BarElement,
	Title,
	Tooltip,
	Legend,
} from "chart.js";
import { Line, Bar } from "react-chartjs-2";

// Chart.js 컴포넌트 등록
ChartJS.register(
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	BarElement,
	Title,
	Tooltip,
	Legend
);

export default function Home() {
	const { getMonthTicketInfo } = useHomeStore();
	const { user } = useAuthStore();
	const [stats, setStats] = useState({
		totalTickets: 0,
		assignedSeats: 0,
		pendingSeats: 0,
	});
	const [loading, setLoading] = useState(true);
	const [chartData, setChartData] = useState(null);
	const [chartLoading, setChartLoading] = useState(true);
	const [activeTab, setActiveTab] = useState("daily");
	const [monthlyRawData, setMonthlyRawData] = useState(null);
	const [dailyRawData, setDailyRawData] = useState(null);

	// API 호출이 이미 진행되었는지 확인하기 위한 ref
	const apiCallCompleted = useRef(false);

	// 현재 날짜 및 시간 정보 가져오기
	const currentDate = new Date();
	const currentMonth = currentDate.toLocaleString("ko-KR", { month: "long" });
	const currentYear = currentDate.getFullYear();
	const currentDay = currentDate.getDate();
	const hours = currentDate.getHours();
	const amPm = hours < 12 ? "오전" : "오후";
	const displayHours = hours % 12 || 12; // 12시는 0이 되기 때문에 12로 변환
	const formattedDateTime = `${currentMonth} ${currentDay}일 ${amPm} ${displayHours}시 기준`;

	// 차트 데이터셋 공통 스타일 생성
	const buildChartDatasets = (ticketData, assignedSeatsData, notAssignedSeatsData) => ({
		datasets: [
			{
				label: "승차권 수",
				data: ticketData,
				borderColor: "rgb(59, 130, 246)",
				backgroundColor: "rgba(59, 130, 246, 0.2)",
				borderWidth: 2,
				pointBackgroundColor: "rgb(59, 130, 246)",
				pointRadius: 3,
				pointHoverRadius: 5,
				tension: 0.4,
				fill: true,
				yAxisID: "y",
			},
			{
				label: "배정된 좌석",
				data: assignedSeatsData,
				borderColor: "rgb(16, 185, 129)",
				backgroundColor: "rgba(16, 185, 129, 0.2)",
				borderWidth: 2,
				pointBackgroundColor: "rgb(16, 185, 129)",
				pointRadius: 3,
				pointHoverRadius: 5,
				tension: 0.4,
				fill: true,
				yAxisID: "y1",
			},
			{
				label: "미배정 좌석",
				data: notAssignedSeatsData,
				borderColor: "rgb(245, 158, 11)",
				backgroundColor: "rgba(245, 158, 11, 0.2)",
				borderWidth: 2,
				pointBackgroundColor: "rgb(245, 158, 11)",
				pointRadius: 3,
				pointHoverRadius: 5,
				tension: 0.4,
				fill: true,
				yAxisID: "y1",
			},
		],
	});

	// 월별 차트 데이터 생성
	const generateMonthlyChartData = useCallback((monthlyData) => {
		if (!monthlyData || monthlyData.length === 0) return null;

		const sortedData = [...monthlyData].sort((a, b) => {
			if (a.year !== b.year) return a.year - b.year;
			return a.month - b.month;
		});

		const labels = sortedData.map((item) => {
			const date = new Date(item.year, item.month - 1, 1);
			const monthName = date.toLocaleString("ko-KR", { month: "short" });
			const isFutureMonth =
				item.year > currentDate.getFullYear() ||
				(item.year === currentDate.getFullYear() &&
					item.month > currentDate.getMonth() + 1);
			return isFutureMonth ? `(${monthName})` : monthName;
		});

		return {
			labels,
			...buildChartDatasets(
				sortedData.map((item) => item.total_ticket_items),
				sortedData.map((item) => item.assigned_seats_count),
				sortedData.map((item) => item.not_assigned_seats_count)
			),
		};
	}, []);

	// 일별 차트 데이터 생성
	const generateDailyChartData = useCallback((dailyData) => {
		if (!dailyData || dailyData.length === 0) return null;

		const sortedData = [...dailyData].sort((a, b) => a.date.localeCompare(b.date));

		const labels = sortedData.map((item) => {
			const d = new Date(item.date + "T00:00:00");
			const mm = String(d.getMonth() + 1).padStart(2, "0");
			const dd = String(d.getDate()).padStart(2, "0");
			return `${mm}/${dd}`;
		});

		return {
			labels,
			...buildChartDatasets(
				sortedData.map((item) => item.total_ticket_items),
				sortedData.map((item) => item.assigned_seats_count),
				sortedData.map((item) => item.not_assigned_seats_count)
			),
		};
	}, []);

	// 현재 탭에 맞는 통계 카드 데이터 업데이트
	const updateStatsForTab = useCallback((tab, monthly, daily) => {
		if (tab === "daily" && daily && daily.length > 0) {
			const todayStr = new Date().toISOString().slice(0, 10);
			const todayData = daily.find((item) => item.date === todayStr);
			if (todayData) {
				setStats({
					totalTickets: todayData.total_ticket_items,
					assignedSeats: todayData.assigned_seats_count,
					pendingSeats: todayData.not_assigned_seats_count,
				});
			} else {
				setStats({ totalTickets: 0, assignedSeats: 0, pendingSeats: 0 });
			}
		} else if (tab === "monthly" && monthly && monthly.length > 0) {
			const cy = new Date().getFullYear();
			const cm = new Date().getMonth() + 1;
			const currentMonthData = monthly.find(
				(item) => item.year === cy && item.month === cm
			);
			if (currentMonthData) {
				setStats({
					totalTickets: currentMonthData.total_ticket_items,
					assignedSeats: currentMonthData.assigned_seats_count,
					pendingSeats: currentMonthData.not_assigned_seats_count,
				});
			} else {
				setStats({ totalTickets: 0, assignedSeats: 0, pendingSeats: 0 });
			}
		}
	}, []);

	// 탭 변경 핸들러
	const handleTabChange = useCallback(
		async (tab) => {
			setActiveTab(tab);
			setChartLoading(true);

			if (tab === "daily") {
				if (dailyRawData) {
					setChartData(generateDailyChartData(dailyRawData));
					updateStatsForTab("daily", monthlyRawData, dailyRawData);
					setChartLoading(false);
				} else {
					const result = await getMonthTicketInfo("daily");
					if (result.success && result.data) {
						setDailyRawData(result.data);
						setChartData(generateDailyChartData(result.data));
						updateStatsForTab("daily", monthlyRawData, result.data);
					}
					setChartLoading(false);
				}
			} else {
				if (monthlyRawData) {
					setChartData(generateMonthlyChartData(monthlyRawData));
					updateStatsForTab("monthly", monthlyRawData, dailyRawData);
					setChartLoading(false);
				} else {
					const result = await getMonthTicketInfo("monthly");
					if (result.success && result.data) {
						setMonthlyRawData(result.data);
						setChartData(generateMonthlyChartData(result.data));
						updateStatsForTab("monthly", result.data, dailyRawData);
					}
					setChartLoading(false);
				}
			}
		},
		[dailyRawData, monthlyRawData, getMonthTicketInfo, generateDailyChartData, generateMonthlyChartData, updateStatsForTab]
	);

	useEffect(() => {
		if (apiCallCompleted.current) return;
		apiCallCompleted.current = true;

		const fetchStats = async () => {
			try {
				setLoading(true);
				setChartLoading(true);

				// 일별/월별 데이터 동시 호출
				const [dailyResult, monthlyResult] = await Promise.all([
					getMonthTicketInfo("daily"),
					getMonthTicketInfo("monthly"),
				]);

				const daily = dailyResult.success ? dailyResult.data : [];
				const monthly = monthlyResult.success ? monthlyResult.data : [];

				setDailyRawData(daily);
				setMonthlyRawData(monthly);

				// 기본 탭은 daily
				updateStatsForTab("daily", monthly, daily);

				if (daily && daily.length > 0) {
					setChartData(generateDailyChartData(daily));
				}

				setLoading(false);
				setChartLoading(false);
			} catch (error) {
				console.error("통계 데이터 로딩 실패:", error);
				setLoading(false);
				setChartLoading(false);
			}
		};

		fetchStats();
	}, [getMonthTicketInfo]);

	const statsLabel =
		activeTab === "daily" ? "오늘 출발 예정" : `${currentMonth} 출발 예정`;

	return (
		<div className="py-8 px-4">
			<div className="bg-white border-l-4 border-blue-500 p-6 rounded-lg shadow-sm mb-8 flex justify-between items-center">
				<div>
					<h1 className="text-3xl font-bold text-gray-800 mb-1">
						안녕하세요,{" "}
						<span className="text-blue-600">
							{user?.id || "관리자"}
						</span>
						님
					</h1>
					<p className="text-gray-500">오늘도 좋은 하루 되세요!</p>
				</div>
				<div className="hidden md:block">
					<IoTicketOutline className="h-16 w-16 text-blue-500 opacity-20" />
				</div>
			</div>

			<div className="mb-8">
				<div className="flex items-center justify-between mb-4">
					<h2 className="text-xl font-semibold text-gray-800">
						<span className="text-blue-600">
							{activeTab === "daily"
								? `${currentYear}년 ${currentMonth} ${currentDay}일`
								: `${currentYear}년 ${currentMonth}`}
						</span>{" "}
						승차권 현황
					</h2>
					<span className="bg-blue-100 text-blue-800 text-xs font-medium px-3 py-1 rounded-full">
						{formattedDateTime}
					</span>
				</div>

				{loading ? (
					<div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
						{[1, 2, 3].map((i) => (
							<div
								key={i}
								className="bg-white overflow-hidden shadow rounded-lg animate-pulse"
							>
								<div className="px-4 py-5 sm:p-6">
									<div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
									<div className="h-8 bg-gray-200 rounded w-1/2"></div>
								</div>
							</div>
						))}
					</div>
				) : (
					<div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
						<div className="bg-white overflow-hidden shadow-md rounded-xl border border-gray-100">
							<div className="px-6 py-6 flex items-center justify-between">
								<div>
									<dt className="text-sm font-medium text-gray-500 truncate mb-1">
										총 승차권 수
									</dt>
									<dd className="text-3xl font-bold text-gray-900">
										{stats.totalTickets}
									</dd>
									<p className="text-xs text-gray-500 mt-2">
										{statsLabel}
									</p>
								</div>
								<div className="bg-blue-500 bg-opacity-10 p-3 rounded-lg">
									<FaTicketAlt className="h-8 w-8 text-blue-500" />
								</div>
							</div>
							<div className="h-1 w-full bg-blue-500"></div>
						</div>

						<div className="bg-white overflow-hidden shadow-md rounded-xl border border-gray-100">
							<div className="px-6 py-6 flex items-center justify-between">
								<div>
									<dt className="text-sm font-medium text-gray-500 truncate mb-1">
										배정된 좌석
									</dt>
									<dd className="text-3xl font-bold text-gray-900">
										{stats.assignedSeats}
									</dd>
									<p className="text-xs text-gray-500 mt-2">
										좌석 배정 완료
									</p>
								</div>
								<div className="bg-green-500 bg-opacity-10 p-3 rounded-lg">
									<LuTrainFront className="h-8 w-8 text-green-500" />
								</div>
							</div>
							<div className="h-1 w-full bg-green-500"></div>
						</div>

						<div className="bg-white overflow-hidden shadow-md rounded-xl border border-gray-100">
							<div className="px-6 py-6 flex items-center justify-between">
								<div>
									<dt className="text-sm font-medium text-gray-500 truncate mb-1">
										배정 대기 좌석
									</dt>
									<dd className="text-3xl font-bold text-gray-900">
										{stats.pendingSeats}
									</dd>
									<p className="text-xs text-gray-500 mt-2">
										좌석 미배정
									</p>
								</div>
								<div className="bg-amber-500 bg-opacity-10 p-3 rounded-lg">
									<FaClock className="h-8 w-8 text-amber-500" />
								</div>
							</div>
							<div className="h-1 w-full bg-amber-500"></div>
						</div>
					</div>
				)}
			</div>

			<div className="mb-8 bg-white shadow-lg rounded-xl overflow-hidden border border-gray-100 p-6">
				<div className="flex items-center justify-between mb-6">
					<div className="flex items-center">
						<span className="inline-block w-3 h-3 bg-blue-500 rounded-full mr-2"></span>
						<h3 className="text-xl font-semibold text-gray-800">
							승차권 통계
						</h3>
					</div>
					<div className="flex bg-gray-100 rounded-lg p-0.5">
						<button
							onClick={() => handleTabChange("daily")}
							className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all duration-200 ${
								activeTab === "daily"
									? "bg-white text-blue-600 shadow-sm"
									: "text-gray-500 hover:text-gray-700"
							}`}
						>
							최근 2주
						</button>
						<button
							onClick={() => handleTabChange("monthly")}
							className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all duration-200 ${
								activeTab === "monthly"
									? "bg-white text-blue-600 shadow-sm"
									: "text-gray-500 hover:text-gray-700"
							}`}
						>
							최근 1년
						</button>
					</div>
				</div>

				{chartLoading ? (
					<div className="h-80 flex items-center justify-center">
						<div className="animate-pulse flex flex-col items-center">
							<div className="h-6 w-32 bg-gray-200 rounded mb-4"></div>
							<div className="h-64 w-full bg-gray-100 rounded"></div>
						</div>
					</div>
				) : (
					chartData && (
						<div className="h-80">
							<Line
								data={chartData}
								options={{
									responsive: true,
									maintainAspectRatio: false,
									scales: {
										y: {
											beginAtZero: true,
											border: {
												display: false,
											},
											grid: {
												color: "rgba(0, 0, 0, 0.03)",
												lineWidth: 0.5,
											},
											ticks: {
												font: {
													size: 10,
												},
												color: "#999",
											},
											position: "left",
										},
										y1: {
											beginAtZero: true,
											border: {
												display: false,
											},
											grid: {
												display: false,
											},
											ticks: {
												font: {
													size: 10,
												},
												color: "#999",
											},
											position: "right",
										},
										x: {
											border: {
												display: false,
											},
											grid: {
												display: false,
											},
											ticks: {
												font: {
													size: 10,
												},
												color: "#999",
											},
										},
									},
									plugins: {
										legend: {
											position: "top",
											align: "end",
											labels: {
												boxWidth: 8,
												usePointStyle: true,
												pointStyle: "circle",
												padding: 15,
												font: {
													size: 11,
												},
											},
										},
										tooltip: {
											backgroundColor:
												"rgba(255, 255, 255, 0.95)",
											titleColor: "#333",
											bodyColor: "#666",
											borderColor:
												"rgba(200, 200, 200, 0.5)",
											borderWidth: 1,
											padding: 10,
											boxPadding: 4,
											cornerRadius: 8,
											titleFont: {
												size: 12,
												weight: "bold",
											},
											bodyFont: {
												size: 11,
											},
											displayColors: true,
											usePointStyle: true,
										},
									},
									interaction: {
										intersect: false,
										mode: "index",
									},
									elements: {
										line: {
											borderWidth: 2,
										},
										point: {
											hitRadius: 6,
										},
									},
								}}
							/>
						</div>
					)
				)}
			</div>

			<div className="bg-white shadow-lg rounded-xl overflow-hidden border border-gray-100">
				<div className="px-6 py-6">
					<div className="flex items-center mb-6">
						<span className="inline-block w-3 h-3 bg-blue-500 rounded-full mr-2"></span>
						<h3 className="text-xl font-semibold text-gray-800">
							빠른 작업
						</h3>
					</div>
					<div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
						<Link
							to="/ticket/register"
							className="group flex items-start p-5 bg-white border border-gray-200 rounded-xl shadow-sm transition-all duration-200 hover:shadow-md hover:border-blue-300 hover:translate-y-[-2px]"
						>
							<div className="mr-4 p-3 bg-blue-100 rounded-lg group-hover:bg-blue-200 transition-colors duration-200">
								<IoTicketOutline className="h-6 w-6 text-blue-600" />
							</div>
							<div>
								<h4 className="text-base font-medium text-gray-800 mb-1 group-hover:text-blue-700 transition-colors duration-200">
									승차권 등록
								</h4>
								<p className="text-sm text-gray-500">
									새로운 승차권을 시스템에 등록합니다.
								</p>
							</div>
						</Link>
						<Link
							to="/ticket/status"
							className="group flex items-start p-5 bg-white border border-gray-200 rounded-xl shadow-sm transition-all duration-200 hover:shadow-md hover:border-blue-300 hover:translate-y-[-2px]"
						>
							<div className="mr-4 p-3 bg-green-100 rounded-lg group-hover:bg-green-200 transition-colors duration-200">
								<GiBookshelf className="h-6 w-6 text-green-600" />
							</div>
							<div>
								<h4 className="text-base font-medium text-gray-800 mb-1 group-hover:text-green-700 transition-colors duration-200">
									승차권 등록 현황
								</h4>
								<p className="text-sm text-gray-500">
									등록된 승차권의 현황을 확인합니다.
								</p>
							</div>
						</Link>
						<Link
							to="/ticket/daily"
							className="group flex items-start p-5 bg-white border border-gray-200 rounded-xl shadow-sm transition-all duration-200 hover:shadow-md hover:border-blue-300 hover:translate-y-[-2px]"
						>
							<div className="mr-4 p-3 bg-indigo-100 rounded-lg group-hover:bg-indigo-200 transition-colors duration-200">
								<IoCalendarNumberOutline className="h-6 w-6 text-indigo-600" />
							</div>
							<div>
								<h4 className="text-base font-medium text-gray-800 mb-1 group-hover:text-indigo-700 transition-colors duration-200">
									일자별 승차권 보유 현황
								</h4>
								<p className="text-sm text-gray-500">
									일자별 승차권 보유 상태를 확인합니다.
								</p>
							</div>
						</Link>
						<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
							<Link
								to="/event/seat"
								className="group flex items-start p-5 bg-white border border-gray-200 rounded-xl shadow-sm transition-all duration-200 hover:shadow-md hover:border-blue-300 hover:translate-y-[-2px]"
							>
								<div className="mr-4 p-3 bg-amber-100 rounded-lg group-hover:bg-amber-200 transition-colors duration-200">
									<LuTrainFront className="h-6 w-6 text-amber-600" />
								</div>
								<div>
									<h4 className="text-base font-medium text-gray-800 mb-1 group-hover:text-amber-700 transition-colors duration-200">
										열차 좌석 배정
									</h4>
									<p className="text-sm text-gray-500">
										열차 행사 좌석을 배정합니다.
									</p>
								</div>
							</Link>
							<Link
								to="/bus/seat"
								className="group flex items-start p-5 bg-white border border-gray-200 rounded-xl shadow-sm transition-all duration-200 hover:shadow-md hover:border-blue-300 hover:translate-y-[-2px]"
							>
								<div className="mr-4 p-3 bg-teal-100 rounded-lg group-hover:bg-teal-200 transition-colors duration-200">
									<FaBus className="h-6 w-6 text-teal-600" />
								</div>
								<div>
									<h4 className="text-base font-medium text-gray-800 mb-1 group-hover:text-teal-700 transition-colors duration-200">
										버스 좌석 배정
									</h4>
									<p className="text-sm text-gray-500">
										버스 좌석을 배정합니다.
									</p>
								</div>
							</Link>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
